
import {RenderContext} from './RenderContext';
import './ChartOptions';
import {IChartOptions} from './ChartOptions';
import {MarkerType} from './Marker';

export interface IRenderer<T extends RenderContext> {
		render(context: T, options: IChartOptions);
	}

	export class Renderer implements IRenderer<RenderContext> {

		private _cache = {
			background: undefined,
			width: 0,
			height: 0
		};

		public render(context: RenderContext, options: IChartOptions) {
			this.renderBackground(context, options);
			this.renderGrid(context, options);
			this.renderSeries(context, options);
			this.renderMarkers(context, options);
		}

		public renderBackground(context: RenderContext, options: IChartOptions) {
			var ctx = context.getContext('background', 0);
			var width = ctx.canvas.width;
			var height = ctx.canvas.height;

			if(this._cache.background == options.background
				&& this._cache.width === width
				&& this._cache.height === height) {
					return;
			}

			if(options.background == 'transparent') {
				ctx.clearRect(0, 0, width, height);
			}
			else {
				ctx.fillStyle = options.background;
				ctx.fillRect(0, 0, width, height);
			}
			this._cache.background = options.background;
			this._cache.width = width;
			this._cache.height = height;
		}

		public renderGrid(context: RenderContext, options:IChartOptions) {

			var gridMajorTransparency = 1;
			var gridMinorTransparency = 0.5;
			var layout = context.getComponentLayout();
			var ctx = context.getContext('grid', 2);
			var width = layout.plot.width;
			var height = layout.plot.height;
			var top = layout.plot.top;
			var left = layout.plot.left;
			var right = left + width;
			var bottom = top + height;
			ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

			var color = options.grid.color || 'grey';
			var display = options.grid.display || 'none';

			//vertical grid
			var min = context.gridConfiguration.vertical.min;
			var max = context.gridConfiguration.vertical.max;
			var scale = context.gridConfiguration.vertical.scale;
			var majorStep = context.gridConfiguration.vertical.majorStep * scale;
			var minorStep = context.gridConfiguration.vertical.minorStep * scale;

			if(majorStep) {

				ctx.fillStyle = color;

				if(min < 0 && 0 < max) {
					//render OX line
					var OX = left + width - min * scale;

					ctx.fillStyle = color;
					// render grid (OX, left)
					var lastMajorStep = OX;
					for(var i=OX-majorStep; i>left; i-=majorStep) {
						ctx.fillRect(Math.round(i), top, 1, height);

						//minor grid
						if(minorStep) {
							ctx.globalAlpha = gridMinorTransparency;;
							for(var j=lastMajorStep; j>i; j-=minorStep) {
								ctx.fillRect(Math.round(j), top, 1, height);
							}
							ctx.globalAlpha = gridMajorTransparency;
						}
						lastMajorStep = i;
					}

					if(minorStep) {
						ctx.globalAlpha = gridMinorTransparency;
						for(var j=lastMajorStep; j>left; j-=minorStep) {
							ctx.fillRect(Math.round(j), top, 1, height);
						}
						ctx.globalAlpha = gridMajorTransparency;
					}

					//render grid (OX, right)
					var lastMajorStep = OX;
					for(var i=OX+majorStep; i<right; i+=majorStep) {
						ctx.fillRect(Math.round(i), top, 1, height);

						//minor grid
						if(minorStep) {
							ctx.globalAlpha = gridMinorTransparency;
							for(var j=lastMajorStep; j<i; j+=minorStep) {
								ctx.fillRect(Math.round(j), top, 1, height);
							}
							ctx.globalAlpha = gridMajorTransparency;
						}
						lastMajorStep = i;
					}

					if(minorStep) {
						ctx.globalAlpha = gridMinorTransparency;
						for(var j=lastMajorStep; j<right; j+=minorStep) {
							ctx.fillRect(Math.round(j), top, 1, height);
						}
						ctx.globalAlpha = gridMajorTransparency;
					}

					//OX line
					ctx.fillStyle = "red";
					ctx.fillRect(Math.round(OX), top, 1, height);

				}
				else { // ! min < 0 < max
					//render grid from min to max
					var roundedMin = Math.floor(min / context.gridConfiguration.vertical.majorStep)
						* context.gridConfiguration.vertical.majorStep;
					var lastMajorStep = (roundedMin - min) * scale;

					for(var i=lastMajorStep; i<right; i+=majorStep) {
						if(i > left){
							ctx.fillRect(Math.round(i), top, 1, height);
						}

						//minor grid
						if(minorStep) {
							ctx.globalAlpha = gridMinorTransparency;;
							for(var j=lastMajorStep; j<i; j+=minorStep) {
								if(j > left) {
									ctx.fillRect(Math.round(j), top, 1, height);
								}
							}
							ctx.globalAlpha = gridMajorTransparency;
						}
						lastMajorStep = i;
					}

					if(minorStep) {
						ctx.globalAlpha = gridMinorTransparency;;
						for(var j=lastMajorStep; j<right; j+=minorStep) {
							ctx.fillRect(Math.round(j), top, 1, height);
						}
						ctx.globalAlpha = gridMajorTransparency;
					}
				}

			}

			//horizontal grid
			var min = context.gridConfiguration.horizontal.min;
			var max = context.gridConfiguration.horizontal.max;
			var scale = context.gridConfiguration.horizontal.scale;
			var majorStep = context.gridConfiguration.horizontal.majorStep * scale;
			var minorStep = context.gridConfiguration.horizontal.minorStep * scale;

			if(majorStep) {

				ctx.fillStyle = color;

				if(min < 0 && 0 < max) {
					//render OY line
					var OY = bottom + min * scale;

					ctx.fillStyle = color;

					// render grid (OY, top)
					var lastMajorStep = OY;
					for(var i=OY-majorStep; i>top; i-=majorStep) {
						ctx.fillRect(left, Math.round(i), width, 1);

						if(minorStep) {
							//minor grid
							ctx.globalAlpha = gridMinorTransparency;;
							for(var j=lastMajorStep; j>i; j-=minorStep) {
								ctx.fillRect(left, Math.round(j), width, 1);
							}
							ctx.globalAlpha = gridMajorTransparency;
						}
						lastMajorStep = i;
					}

					if(minorStep) {
						ctx.globalAlpha = gridMinorTransparency;;
						for(var j=lastMajorStep; j>top; j-=minorStep) {
							ctx.fillRect(left, Math.round(j), width, 1);
						}
						ctx.globalAlpha = 1;
					}

					//render grid (OY, bottom)
					var lastMajorStep = OY;
					for(var i=OY+majorStep; i<bottom; i+=majorStep) {
						ctx.fillRect(left, Math.round(i), width, 1);
						if(minorStep) {
							//minor grid
							ctx.globalAlpha = gridMinorTransparency;;
							for(var j=lastMajorStep; j<i; j+=minorStep) {
								ctx.fillRect(left, Math.round(j), width, 1);
							}
							ctx.globalAlpha = gridMajorTransparency;
						}
						lastMajorStep = i;
					}

					if(minorStep) {
						ctx.globalAlpha = gridMinorTransparency;;
						for(var j=lastMajorStep; j<bottom; j+=minorStep) {
							ctx.fillRect(left, Math.round(j), width, 1);
						}
						ctx.globalAlpha = gridMajorTransparency;
					}

					ctx.fillStyle = "red";
					ctx.fillRect(left, Math.round(OY), width, 1);
				}
				else {

					if(majorStep) {
						//render grid from min to max
						var lastMajorStep = bottom;
						for(var i=lastMajorStep; i>top; i-=majorStep) {
							ctx.fillRect(left, Math.round(i), width, 1);
							if(minorStep) {
								//minor grid
								ctx.globalAlpha = gridMinorTransparency;
								for(var j=lastMajorStep; j>i; j-=minorStep) {
									ctx.fillRect(left, Math.round(j), width, 1);
								}
								ctx.globalAlpha = gridMajorTransparency;
							}
							lastMajorStep = i;
						}
						if(minorStep) {
							ctx.globalAlpha = gridMinorTransparency;
							for(var j=lastMajorStep; j>top; j-=minorStep) {
								ctx.fillRect(left, Math.round(j), width, 1);
							}
							ctx.globalAlpha = gridMajorTransparency;
						}
					}

				}

			}
		}

		public renderSeries(context: RenderContext, options: IChartOptions) {
			var ctx = context.getContext('series', 3);
			ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
			var layout = context.getComponentLayout();
			var width = layout.plot.width;
			var height = layout.plot.height;
			var top = layout.plot.top;
			var left = layout.plot.left;
			var right = left + width;
			var bottom = top + height;

			// ctx.strokeRect(Math.round(left), Math.round(top), Math.round(width), Math.round(height));

			var xAxis = context.axesConfiguration.xAxes[0];
			var yAxis = context.axesConfiguration.yAxes[0];

			context.seriesConfiguration.series.forEach(series => {

				ctx.strokeStyle = series.color || options.seriesColor;
				ctx.beginPath();
				var prevCoords = undefined;
				series.values.forEach(item => {
					var xValue = series.valueProvider.getX(item);
					var yValue = series.valueProvider.getY(item);

					if(item === null || item === undefined || yValue === null || yValue === undefined) {
						// add gaps on null values
						ctx.stroke();
						ctx.beginPath();
						prevCoords = null;
					}

					if(xAxis.minimum <= xValue && xValue <= xAxis.maximum) {
						var coords = {
							x: left + ( xValue - xAxis.minimum)  * xAxis.scaleFactor,
							y: bottom - (yValue - yAxis.minimum) * yAxis.scaleFactor
						};
						(prevCoords) ?
							ctx.moveTo(prevCoords.x, prevCoords.y) :
							ctx.moveTo(coords.x, coords.y);
						ctx.lineTo(coords.x, coords.y);
						prevCoords = coords;
					}
				});
				ctx.stroke();
			});

		}

		public renderMarkers(context: RenderContext, options: IChartOptions){

			var ctx = context.getContext('markers-front', 4);
			ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

			var ctxBack = context.getContext('markers-back', 1);
			ctxBack.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

			if(!context.markers || context.markers.length == 0){
				return;
			}

			var layout = context.getComponentLayout();
			var width = layout.plot.width;
			var height = layout.plot.height;
			var top = layout.plot.top;
			var left = layout.plot.left;
			var right = left + width;
			var bottom = top + height;

			var xAxis = context.axesConfiguration.xAxes[0];
			var yAxis = context.axesConfiguration.yAxes[0];

			//draw horizontal timeline
			ctx.fillStyle = options.timeline.color;
			ctx.fillRect(left, bottom - options.timeline.strokeWidth, width, options.timeline.strokeWidth);

			//draw markers
			var radius = 5;
			context.markers.forEach(marker => {

				if(marker.type == MarkerType.HIGHLIGHT){

					var coords:any = {
						x: left + ( marker.timestamp - xAxis.minimum)  * xAxis.scaleFactor,
						y: top,
						width: (marker.value || 1) * xAxis.scaleFactor,
						height: height
					};

					// no transparency not to show overlapped markers
					//ctxBack.globalAlpha = 0.5;
					ctxBack.fillStyle = marker.color;
					ctxBack.fillRect(coords.x, coords.y, coords.width, coords.height);
					//ctxBack.globalAlpha = 1;
				}
				else //if(marker.type == dataset.MarkerType.CIRCLE){
				{
					var coords:any = {
						x: left + ( marker.timestamp - xAxis.minimum)  * xAxis.scaleFactor,
						y: (marker.value !== undefined)
							? bottom - (marker.value  - yAxis.minimum) * yAxis.scaleFactor
							: bottom
					};

					ctx.beginPath();
					ctx.arc(coords.x, coords.y, radius, 0, 2 * Math.PI, false);
					ctx.fillStyle = marker.color;
					ctx.fill();
					ctx.closePath();
				}

			});
		}

	}
